import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Visibility as VisibilityIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  deleteDoc,
} from '../../firebase/firebase';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';

const MessageManagement = () => {
  // 获取认证上下文
  const { currentUser, isAdmin } = useAuth();
  
  // State management
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessageDetails, setSelectedMessageDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
  });
  const [error, setError] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  // Load messages data
  const loadMessages = async (reset = false) => {
    // 检查用户是否为管理员
    if (!isAdmin) {
      setError('您没有访问此页面的权限。');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(reset);
      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      // 确保有管理员权限
      console.log('正在加载消息数据，当前用户权限: ', { currentUser: !!currentUser, isAdmin });
      
      let messagesQuery = collection(db, 'mail');
      let constraints = [];

      if (statusFilter !== 'all') {
        const isRead = statusFilter === 'read';
        constraints.push(where('read', '==', isRead));
      }

      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(10));

      if (lastVisible && !reset) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(messagesQuery, ...constraints);
      
      try {
        console.log("执行Firestore查询", { statusFilter, constraints });
        const querySnapshot = await getDocs(q);
        console.log("查询结果", { empty: querySnapshot.empty, size: querySnapshot.size });
        
        if (querySnapshot.empty) {
          setHasMore(false);
          setLoadingMore(false);
          setLoading(false);
          return;
        }

        const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
        setLastVisible(newLastVisible);

        const newMessages = querySnapshot.docs.map(doc => {
          console.log("邮件数据", doc.id, doc.data());
          return {
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().createdAt?.toDate(),
          };
        });

        if (reset) {
          setMessages(newMessages);
        } else {
          setMessages(prev => [...prev, ...newMessages]);
        }

        setHasMore(querySnapshot.docs.length === 10);
      } catch (firestoreError) {
        console.error('Firestore查询错误:', firestoreError);
        setError('Firestore查询错误: ' + firestoreError.message);
        
        if (firestoreError.code === 'permission-denied') {
          setSnackbar({
            open: true,
            message: '权限不足: 请确保您有管理员权限并且Firestore规则已正确配置',
            severity: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            message: '加载消息数据失败: ' + firestoreError.message,
            severity: 'error',
          });
        }
      }
    } catch (error) {
      console.error('加载消息数据失败:', error);
      setError('加载消息数据失败: ' + error.message);
      setSnackbar({
        open: true,
        message: '加载消息数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load and when filters change or admin status changes
  useEffect(() => {
    // 只在用户是管理员时加载数据
    if (currentUser && isAdmin) {
      loadMessages(true);
    }
  }, [statusFilter, currentUser, isAdmin]);

  // Handle search
  const handleSearch = () => {
    if (searchTerm.trim() !== '') {
      const filtered = messages.filter(
        message => 
          (message.from && message.from.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (message.subject && message.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (message.content && message.content.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setMessages(filtered);
      setHasMore(false);
    } else {
      loadMessages(true);
    }
  };

  // View message details
  const handleViewMessage = async (id) => {
    // 检查用户是否为管理员
    if (!currentUser || !isAdmin) {
      setSnackbar({
        open: true,
        message: '您没有权限查看消息详情',
        severity: 'error',
      });
      return;
    }
    
    try {
      setLoading(true);
      
      try {
        const messageDoc = await getDoc(doc(db, 'mail', id));
        
        if (messageDoc.exists()) {
          const messageData = messageDoc.data();
          setSelectedMessageDetails(messageData);
          setSelectedMessage(id);
          setOpenDetailsDialog(true);
          
          // Mark as read if it's not already
          if (!messageData.read) {
            try {
              await updateDoc(doc(db, 'mail', id), {
                read: true,
                readTimestamp: serverTimestamp(),
              });
              
              // Update the messages list to show read status
              setMessages(prevMessages => 
                prevMessages.map(msg => 
                  msg.id === id ? { ...msg, read: true } : msg
                )
              );
            } catch (updateError) {
              console.error('更新消息已读状态失败:', updateError);
              setSnackbar({
                open: true,
                message: '更新消息已读状态失败: ' + updateError.message,
                severity: 'warning',
              });
              
              // 即使更新失败，仍然显示消息详情
            }
          }
        } else {
          setSnackbar({
            open: true,
            message: '找不到消息数据',
            severity: 'error',
          });
        }
      } catch (docError) {
        console.error('获取消息详情失败:', docError);
        
        if (docError.code === 'permission-denied') {
          setSnackbar({
            open: true,
            message: '权限不足: 请确保您有管理员权限并且Firestore规则已正确配置',
            severity: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            message: '获取消息详情失败: ' + docError.message,
            severity: 'error',
          });
        }
      }
    } catch (error) {
      console.error('加载消息数据失败:', error);
      setSnackbar({
        open: true,
        message: '加载消息数据失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Mark as read/unread
  const handleToggleReadStatus = async (id, currentReadStatus) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'mail', id), {
        read: !currentReadStatus,
        ...(currentReadStatus ? {} : { readTimestamp: serverTimestamp() }),
      });
      
      // Update local state
      setMessages(messages.map(message => {
        if (message.id === id) {
          return {
            ...message,
            read: !currentReadStatus,
          };
        }
        return message;
      }));
      
      setSnackbar({
        open: true,
        message: `消息已标记为${!currentReadStatus ? '已读' : '未读'}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('更新消息状态失败:', error);
      setSnackbar({
        open: true,
        message: '更新消息状态失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = (messageId) => {
    setConfirmDialog({
      open: true,
      title: '确认删除',
      message: '确定要删除这条消息吗？此操作不可撤销。',
      action: async () => {
        try {
          setLoading(true);
          await deleteDoc(doc(db, 'mail', messageId));
          
          // Update local state
          setMessages(messages.filter(message => message.id !== messageId));
          
          setSnackbar({
            open: true,
            message: '消息已删除',
            severity: 'success',
          });
          
          if (openDetailsDialog && selectedMessage === messageId) {
            setOpenDetailsDialog(false);
          }
        } catch (error) {
          console.error('删除消息失败:', error);
          setSnackbar({
            open: true,
            message: '删除消息失败: ' + error.message,
            severity: 'error',
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Reply to message
  const handleReply = () => {
    if (!selectedMessageDetails) return;
    
    const subject = selectedMessageDetails.subject 
      ? selectedMessageDetails.subject.replace('网站新联系表单: ', '') 
      : '您的咨询';
    
    const customerName = selectedMessageDetails.name || 
                         (selectedMessageDetails.from ? selectedMessageDetails.from.split('@')[0] : '客户');
    
    setReplyContent(`尊敬的${customerName}，您好！

感谢您的来信。关于您提到的"${subject}"，我们的回复如下：

[请在此输入您的回复内容]

如有任何疑问，请随时与我们联系。

祝好！
DestinyCrystal团队`);
    
    setReplyDialogOpen(true);
  };

  // Send reply
  const handleSendReply = async () => {
    try {
      setLoading(true);
      
      console.log('发送回复', {
        to: selectedMessageDetails.email || selectedMessageDetails.replyTo,
        subject: selectedMessageDetails.subject,
        content: replyContent
      });
      
      // 在真实实现中，你会在这里发送邮件
      // 这里只是为了演示功能，更新了消息状态
      
      // 标记原始消息为已回复
      const updateData = {
        replied: true,
        replyContent: replyContent,
        replyTimestamp: serverTimestamp(),
        status: 'replied',
        read: true,
      };
      
      if (!selectedMessageDetails.readTimestamp) {
        updateData.readTimestamp = serverTimestamp();
      }
      
      try {
        await updateDoc(doc(db, 'mail', selectedMessage), updateData);
        
        // 更新本地状态
        setMessages(messages.map(message => {
          if (message.id === selectedMessage) {
            return {
              ...message,
              replied: true,
              status: 'replied',
              read: true,
            };
          }
          return message;
        }));
        
        if (selectedMessageDetails) {
          setSelectedMessageDetails({
            ...selectedMessageDetails,
            replied: true,
            replyContent: replyContent,
            replyTimestamp: new Date(),
            status: 'replied',
            read: true,
          });
        }
        
        setSnackbar({
          open: true,
          message: '回复已保存，请手动回复邮件至: ' + (selectedMessageDetails.email || selectedMessageDetails.replyTo),
          severity: 'success',
        });
        
        setReplyDialogOpen(false);
      } catch (updateError) {
        console.error('更新消息状态失败:', updateError);
        throw updateError;
      }
    } catch (error) {
      console.error('发送回复失败:', error);
      setSnackbar({
        open: true,
        message: '发送回复失败: ' + error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('zh-CN');
    }
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  // 显示权限错误
  if (!currentUser || !isAdmin) {
    return (
      <Box sx={{ my: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          您没有访问此页面的权限。请使用管理员账户登录。
        </Alert>
      </Box>
    );
  }
  
  // 显示加载指示器
  if (loading && messages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // 显示错误信息
  if (error) {
    return (
      <Box sx={{ my: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => loadMessages(true)}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          disabled={loading}
        >
          重试
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="搜索消息"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>状态</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="状态"
            >
              <MenuItem value="all">全部消息</MenuItem>
              <MenuItem value="unread">未读消息</MenuItem>
              <MenuItem value="read">已读消息</MenuItem>
            </Select>
          </FormControl>

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadMessages(true)}
            color="primary"
            variant="outlined"
            size="medium"
          >
            刷新
          </Button>
        </Box>
      </Box>

      {/* Messages table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="消息列表">
          <TableHead>
            <TableRow>
              <TableCell>状态</TableCell>
              <TableCell>发件人</TableCell>
              <TableCell>邮箱</TableCell>
              <TableCell>主题</TableCell>
              <TableCell>接收时间</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  暂无消息
                </TableCell>
              </TableRow>
            ) : (
              messages.map((message) => (
                <TableRow 
                  key={message.id} 
                  sx={{ 
                    backgroundColor: message.read ? 'inherit' : 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                      backgroundColor: message.read ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.12)',
                    },
                  }}
                >
                  <TableCell>
                    <Chip
                      icon={message.read ? <MarkEmailReadIcon /> : <EmailIcon />}
                      label={message.status || (message.read ? '已读' : '未读')}
                      color={message.read ? 'default' : 'primary'}
                      size="small"
                      onClick={() => handleToggleReadStatus(message.id, message.read)}
                    />
                    {message.replied && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="已回复"
                        color="success"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{message.name || message.from?.split('@')[0] || 'N/A'}</TableCell>
                  <TableCell>{message.email || message.replyTo || 'N/A'}</TableCell>
                  <TableCell>
                    {message.subject ? message.subject.replace('网站新联系表单: ', '') : '无主题'}
                  </TableCell>
                  <TableCell>
                    {formatDate(message.timestamp)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title="查看详情">
                        <IconButton
                          color="info"
                          onClick={() => handleViewMessage(message.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="回复">
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setSelectedMessage(message.id);
                            setSelectedMessageDetails(message);
                            handleReply();
                          }}
                        >
                          <ReplyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="删除">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(message.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load more button */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            onClick={() => loadMessages(false)}
            disabled={loadingMore}
            variant="outlined"
            startIcon={loadingMore ? <CircularProgress size={20} /> : null}
          >
            {loadingMore ? '加载中...' : '加载更多'}
          </Button>
        </Box>
      )}

      {/* Message details dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="message-details-title"
        disableEnforceFocus={false}
      >
        <DialogTitle id="message-details-title">消息详情</DialogTitle>
        <DialogContent dividers>
          {selectedMessageDetails && (
            <Box>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedMessageDetails.subject 
                      ? selectedMessageDetails.subject.replace('网站新联系表单: ', '') 
                      : '无主题'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>发件人:</strong> {selectedMessageDetails.name || selectedMessageDetails.from?.split('@')[0] || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>邮箱:</strong> {selectedMessageDetails.email || selectedMessageDetails.replyTo || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>接收时间:</strong> {formatDate(selectedMessageDetails.timestamp || selectedMessageDetails.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>状态:</strong> {selectedMessageDetails.status || '新消息'}
                      </Typography>
                    </Grid>
                    {selectedMessageDetails.to && selectedMessageDetails.to.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>收件人:</strong> {selectedMessageDetails.to.join(', ')}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    消息内容
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {selectedMessageDetails.html ? (
                    <Box>
                      <Box 
                        sx={{ mb: 2 }}
                        dangerouslySetInnerHTML={{ __html: selectedMessageDetails.html }} 
                      />
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        纯文本内容:
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedMessageDetails.text || selectedMessageDetails.messageContent || '无纯文本内容'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedMessageDetails.text || selectedMessageDetails.messageContent || '无内容'}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {selectedMessageDetails.replied && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      回复内容
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>回复时间:</strong> {selectedMessageDetails.replyTimestamp ? formatDate(selectedMessageDetails.replyTimestamp) : 'N/A'}
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedMessageDetails.replyContent || '无回复内容'}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>关闭</Button>
          <Button
            onClick={handleReply}
            color="primary"
            startIcon={<ReplyIcon />}
            variant="contained"
          >
            回复
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={() => setReplyDialogOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="reply-dialog-title"
        disableEnforceFocus={false}
      >
        <DialogTitle id="reply-dialog-title">回复消息</DialogTitle>
        <DialogContent dividers>
          {selectedMessageDetails && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                回复给: {selectedMessageDetails.name || selectedMessageDetails.from?.split('@')[0] || '客户'} 
                ({selectedMessageDetails.email || selectedMessageDetails.replyTo || 'N/A'})
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                主题: Re: {selectedMessageDetails.subject 
                  ? selectedMessageDetails.subject.replace('网站新联系表单: ', '') 
                  : '您的咨询'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <TextField
                fullWidth
                label="回复内容"
                multiline
                rows={10}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleSendReply}
            color="primary"
            variant="contained"
            disabled={!replyContent.trim() || loading}
          >
            发送回复
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        aria-labelledby="confirm-dialog-title"
        disableEnforceFocus={false}
      >
        <DialogTitle id="confirm-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            取消
          </Button>
          <Button
            onClick={() => {
              confirmDialog.action();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageManagement;